import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography
} from '@mui/material';
import { getCountries, getCountryCallingCode } from 'libphonenumber-js';
import metadata from 'libphonenumber-js/metadata.min.json';
import { useEffect, useState } from 'react';
import 'react-phone-input-2/lib/material.css';
import modal from '../assets/modal.webp';

const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

const initialFormState = {
  name: '',
  phone: '',
  email: '',
  message: '',
};

export default function FormModal({ open, onClose }) {
  const [form, setForm] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [countryList, setCountryList] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('IN'); // default
  const [countryDialCode, setCountryDialCode] = useState('91');

  // Load country list on mount
  useEffect(() => {
    const countries = getCountries(metadata).map((code) => ({
      code,
      name: displayNames.of(code),
      dialCode: getCountryCallingCode(code, metadata)
    }));
    setCountryList(countries);
    if (countries.length) {
      setSelectedCountry('IN');
      setCountryDialCode(getCountryCallingCode('IN', metadata));
    }
  }, []);

  useEffect(() => {
    if (open) {
      setForm(initialFormState);
      setErrors({});
      setSubmitted(false);
      setSelectedCountry('IN');
      setCountryDialCode('91');
    }
  }, [open]);

  const handleCountryChange = (event) => {
    const code = event.target.value;
    const selected = countryList.find(c => c.code === code);
    setSelectedCountry(code);
    setCountryDialCode(selected.dialCode);
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (!/^[A-Za-z\s]+$/.test(value)) return 'Only alphabets allowed';
        break;
      case 'phone':
        if (!value.trim()) return 'Phone number is required';
        if (!/^\d{7,12}$/.test(value)) return 'Must be 7 to 12 digits only';
        break;
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Invalid email format';
        break;
      case 'message':
        if (!value.trim()) return 'Message is required';
        break;
      default:
        return '';
    }
    return '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    const errorMsg = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: errorMsg }));
  };

  const handleSubmit = () => {
    let newErrors = {};
    Object.keys(form).forEach((field) => {
      const error = validateField(field, form[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      const fullPhone = `+${countryDialCode}${form.phone}`;
      console.log('Submitted with Phone:', fullPhone);
      setSubmitted(true);

      setTimeout(() => {
        setSubmitted(false);
        onClose();
        setForm(initialFormState);
        setErrors({});
        setCountryDialCode('91');
      }, 2000);
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: 500,
    backgroundImage: `url(${modal})`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    borderRadius: '12px',
    boxShadow: 24,
    p: 4,
    outline: 'none',
    fontFamily: 'Rajdhani, sans-serif',
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <CloseIcon />
        </IconButton>

        <Typography variant="h6" sx={{ mb: 2, color: '#393838ff' }}>
          Fill the form below and we will send you a notification regarding your query
        </Typography>

        {submitted ? (
          <Typography sx={{ mt: 4, textAlign: 'center', color: 'green' }}>
            Submitted!
          </Typography>
        ) : (
          <>
            <TextField
              fullWidth
              label="Name"
              name="name"
              value={form.name}
              onChange={handleChange}
              error={!!errors.name}
              helperText={errors.name}
              sx={{ mb: 2 }}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Country</InputLabel>
                <Select
                  value={selectedCountry}
                  onChange={handleCountryChange}
                  label="Country"
                >
                  {countryList.map((country) => (
                    <MenuItem key={country.code} value={country.code}>
                      {country.name} (+{country.dialCode})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                inputProps={{ maxLength: 12 }}
              />
            </Box>

            <TextField
              fullWidth
              label="Email"
              name="email"
              value={form.email}
              onChange={handleChange}
              error={!!errors.email}
              helperText={errors.email}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Message"
              name="message"
              value={form.message}
              onChange={handleChange}
              error={!!errors.message}
              helperText={errors.message}
              multiline
              rows={4}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              fullWidth
              color="primary"
              onClick={handleSubmit}
              sx={{ textTransform: 'uppercase', fontWeight: 'bold' }}
            >
              Submit
            </Button>
          </>
        )}
      </Box>
    </Modal>
  );
}
